"use client"

import type { FunctionComponent } from "react"

import ctx from "classnames"
import { useCallback, useEffect, useRef, useState } from "react"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { DotsVertical } from "@jobteaser/spark/components/Icons/DotsVertical"

import { usePageLayoutContext } from "@/modules/jobAdDetail/components/PageLayout/PageLayoutContext"
import { useJobAdDetailTracking } from "@/modules/jobAdDetail/hooks/useJobAdDetailTracking"

import styles from "./DropdownActions.module.css"

type DropdownActionsProps = {
  dropdownLabel: string
  printLabel: string
  reportLabel: string
}

export const DropdownActions: FunctionComponent<DropdownActionsProps> = ({
  reportLabel,
  printLabel,
  dropdownLabel,
}) => {
  const { trackJobAdDetailDotsDropdownClick } = useJobAdDetailTracking()

  const [isDropdownMenuVisible, setIsDropdownMenuVisible] = useState(false)
  const { openReportFlow, printJobAd } = usePageLayoutContext()

  const dropdownRef = useRef<HTMLDivElement>(null)

  const hideDropdownMenu = (): void => {
    setIsDropdownMenuVisible(false)
  }

  const handleDocumentClick = useCallback((event: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      hideDropdownMenu()
    }
  }, [])

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick)
    return () => {
      document.removeEventListener("click", handleDocumentClick)
    }
  }, [handleDocumentClick])

  const toggleDropdownMenu = (): void => {
    trackJobAdDetailDotsDropdownClick(!isDropdownMenuVisible)
    setIsDropdownMenuVisible(!isDropdownMenuVisible)
  }

  const dropdownStyles = ctx(styles.dropdown, styles.dropdownInDesktop)

  return (
    <>
      {/* OPTIMIZE: use Spark dropdown when available */}
      <div className={dropdownStyles} ref={dropdownRef}>
        <IconButton
          variant="outlined"
          icon={DotsVertical}
          data-testid="jobad-DetailView__FooterActions__dropdown"
          onClick={toggleDropdownMenu}
          title={dropdownLabel}
        />
        {isDropdownMenuVisible && (
          <ul className={styles.dropdown__menu}>
            <li className={styles.dropdown__item}>
              <button
                type="button"
                className={styles.dropdown__itemButton}
                onClick={() => {
                  hideDropdownMenu()
                  openReportFlow()
                }}
                data-testid="jobad-DetailView__FooterActions__report"
              >
                {reportLabel}
              </button>
            </li>
            <li className={styles.dropdown__item}>
              <button
                type="button"
                className={styles.dropdown__itemButton}
                onClick={() => {
                  hideDropdownMenu()
                  printJobAd()
                }}
                data-testid="jobad-DetailView__FooterActions__print"
              >
                {printLabel}
              </button>
            </li>
          </ul>
        )}
      </div>
    </>
  )
}
